import React from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.9; }
`;

const Shell = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 320px;
  padding: 0.625rem;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  box-shadow: 0 8px 24px var(--shadow);

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1rem;
  }
`;

const Placeholder = styled.div`
  border-radius: 6px;
  background: color-mix(in srgb, var(--surface) 70%, var(--border));
  animation: ${pulse} 1.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: 8rem minmax(7rem, 1fr) 8rem;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  ${Placeholder}:nth-child(1),
  ${Placeholder}:nth-child(3) {
    height: 2rem;
  }

  ${Placeholder}:nth-child(2) {
    width: min(11rem, 100%);
    height: 1.65rem;
    justify-self: center;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;

    ${Placeholder}:nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 1;
    }
  }
`;

const DateRail = styled(Placeholder)`
  height: 3.5rem;
  margin-bottom: 0.65rem;
`;

const AgendaRows = styled.div`
  display: grid;
  flex: 1;
  grid-template-rows: repeat(3, minmax(5.5rem, 1fr));
  gap: 0.65rem;
  min-height: 240px;

  ${Placeholder} {
    background: color-mix(in srgb, var(--surface) 86%, var(--border));
  }

  @media (max-width: 768px) {
    grid-template-rows: repeat(4, 6.5rem);

    ${Placeholder}:last-child {
      display: block;
    }
  }
`;

const CalendarLoadingShell: React.FC = () => (
  <Shell aria-busy="true" aria-label="Loading calendar events">
    <span className="visually-hidden" role="status">Loading calendar events</span>
    <Toolbar aria-hidden="true">
      <Placeholder />
      <Placeholder />
      <Placeholder />
    </Toolbar>
    <DateRail aria-hidden="true" />
    <AgendaRows aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => <Placeholder key={index} />)}
    </AgendaRows>
  </Shell>
);

export default CalendarLoadingShell;
